/**
 * 상세 하단 판정 바 — 승인/반려/재추출 버튼 (D15).
 *
 * 노출 규칙:
 * - 검수 대기·차단(gate_blocked) 룰: 반려 버튼. 승인 버튼은 검수 대기이거나
 *   "새 기준 차단"(criteria_version 있음)일 때만.
 * - 옛 기준 차단(criteria_version 없음): 승인 버튼 없이 재추출 필요 안내를 보인다(D10).
 * - 그 밖(active·archived): 전이 불가 안내.
 * 재추출 버튼은 상태와 무관하게 늘 보인다.
 *
 * 순수 프리젠테이션 — 확인 창 분기(검수 대기 vs 차단)는 상위(상세 페이지)가 onApprove에서 정한다.
 */
import React from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Button from "components/atoms/Button";
import { doseRuleStatusLabel } from "utils/doseRule";

export interface JudgmentBarProps {
  status: string;
  /** provenance.criteria_version 유무. 옛 기준 차단 룰은 승인 불가(D10). */
  isNewCriteria: boolean;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
  onReextract: () => void;
}

export const LEGACY_GATE_BLOCKED_NOTICE =
  "옛 기준 차단 룰 — 새 기준으로 재추출해야 승인할 수 있습니다";

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px 16px;
  border-top: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
  background-color: ${COLOR_SYSTEM.get("Gray")?.[10]};
`;

const Grow = styled.div`
  flex: 1;
`;

const Note = styled.div`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

const LegacyNotice = styled.div`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Orange")?.[700]};
`;

const JudgmentBar: React.FC<JudgmentBarProps> = ({
  status,
  isNewCriteria,
  busy,
  onApprove,
  onReject,
  onReextract,
}) => {
  const isPending = status === "pending_review";
  const isGateBlocked = status === "gate_blocked";
  const canTransition = isPending || isGateBlocked;
  const canApprove = isPending || (isGateBlocked && isNewCriteria);
  const showLegacyNotice = isGateBlocked && !isNewCriteria;

  return (
    <Bar>
      {canTransition ? (
        <>
          {canApprove ? (
            <Button
              buttonType="button"
              type="Text"
              size="Medium"
              variant="Contained"
              color="Primary"
              disabled={busy}
              disabledColor
              onClick={onApprove}
            >
              승인
            </Button>
          ) : null}
          {showLegacyNotice ? <LegacyNotice>{LEGACY_GATE_BLOCKED_NOTICE}</LegacyNotice> : null}
          <Button
            buttonType="button"
            type="Text"
            size="Medium"
            variant="Outlined"
            color="Negative"
            disabled={busy}
            disabledColor
            onClick={onReject}
          >
            반려
          </Button>
        </>
      ) : (
        <Note>
          {doseRuleStatusLabel(status)} 상태 — 전이는 검수 대기·차단 룰만 가능합니다.
        </Note>
      )}
      <Grow />
      <Button
        buttonType="button"
        type="Text"
        size="Medium"
        variant="Ghost"
        color="Secondary"
        disabled={busy}
        disabledColor
        onClick={onReextract}
      >
        재추출
      </Button>
    </Bar>
  );
};

export default JudgmentBar;
